import { ApiError, uuid } from '../../api/client';
import type { ScriptLine, ScriptProject, ScriptRole } from '../../api/scripts';
import { uiText } from '../../i18n';

export const scriptTextCount = (project: Pick<ScriptProject, 'lines'>) =>
  project.lines.reduce((total, line) => total + [...line.text].length, 0);

export function scriptValidation(project: Pick<ScriptProject, 'name' | 'roles' | 'lines'>): string {
  if ([...project.name].length > 128) return uiText('Название слишком длинное.');
  if (!project.roles.length || project.roles.length > 50) return uiText('В проекте должно быть от 1 до 50 ролей.');
  if (project.roles.some(role => !role.name.trim() || [...role.name].length > 128)) return uiText('Укажите имя каждой роли.');
  if (project.lines.length > 500) return uiText('В проекте может быть не больше 500 реплик.');
  if (project.lines.some(line => [...line.text].length > 1000)) return uiText('Реплика длиннее 1 000 знаков.');
  if (scriptTextCount(project) > 20_000) return uiText('Проект длиннее 20 000 знаков.');
  return '';
}

export function scriptError(error: unknown): string {
  if (!(error instanceof ApiError)) return uiText('Не удалось связаться с сервером. Проверьте соединение и повторите попытку.');
  const known: Record<string, string> = {
    version_conflict: 'Проект изменился в другом окне. Сравните свою версию и загрузите свежую.',
    project_revision_changed: 'Проект изменился. Сохраните его и проверьте цену ещё раз.',
    price_changed: 'Цена изменилась. Проверьте новую цену перед запуском.',
    script_price_changed: 'Цена изменилась. Проверьте новую цену перед запуском.',
    insufficient_tokens: 'Не хватает Circle. Пополните баланс и повторите действие.',
    insufficient_funds: 'Не хватает Circle. Пополните баланс и повторите действие.',
    voice_not_selected: 'Выберите голос для каждой непустой роли.',
    voice_unavailable: 'Один из голосов больше недоступен. Выберите другой голос.',
    script_operation_active: 'Озвучка уже выполняется. Дождитесь результата.',
    script_render_active: 'Озвучка уже выполняется. Дождитесь результата.',
    result_version_conflict: 'Результат изменился. Загрузите его актуальную версию.',
    not_found: 'Проект или результат больше недоступен.',
    project_not_found: 'Проект больше недоступен.',
  };
  if (known[error.code]) return uiText(known[error.code]);
  if (error.status === 412) return uiText(known.version_conflict);
  if (error.status === 404 || error.status === 410) return uiText('Проект или результат больше недоступен.');
  if (error.status === 429) return uiText('Слишком много запросов. Подождите и повторите проверку.');
  if (error.status === 422) return uiText('Проверьте роли, голоса и длину реплик.');
  return uiText('Не удалось выполнить действие. Изменения остаются на экране.');
}

/** Таймкоды отбрасываем; строка без имени продолжает предыдущую роль. */
export function parseScript(text: string, existing: ScriptRole[] = []): { roles: ScriptRole[]; lines: ScriptLine[] } {
  const roles = [...existing];
  const lines: ScriptLine[] = [];
  let current: ScriptRole | undefined = roles[0];
  for (const source of text.split(/\r?\n/)) {
    const clean = source.replace(/^\s*\[?\d{1,2}:\d{2}(?::\d{2})?\]?\s*/, '').trim();
    if (!clean) continue;
    const named = /^([^:\n]{1,128}):\s*(.*)$/.exec(clean);
    let phrase = clean;
    if (named) {
      const name = named[1].trim();
      current = roles.find(role => role.name.toLocaleLowerCase() === name.toLocaleLowerCase());
      if (!current) {
        current = { id: uuid(), name, voice_id: null, voice_source: 'default' };
        roles.push(current);
      }
      phrase = named[2].trim();
    }
    if (!current) {
      current = { id: uuid(), name: uiText('Рассказчик'), voice_id: null, voice_source: 'default' };
      roles.push(current);
    }
    const chars = [...phrase];
    for (let offset = 0; offset < chars.length; offset += 1000)
      lines.push({ id: uuid(), role_id: current.id, text: chars.slice(offset, offset + 1000).join('') });
  }
  if (!roles.length) roles.push({ id: uuid(), name: uiText('Рассказчик'), voice_id: null, voice_source: 'default' });
  return { roles, lines };
}
